import React, { useState } from "react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import Icon from "components/AppIcon";

const cashflowData = [
  { year: "2025", equity: -45, debt: -120, revenue: 0, opex: 0 },
  { year: "2026", equity: -20, debt: -30, revenue: 18, opex: -8 },
  { year: "2027", equity: 0, debt: 0, revenue: 42, opex: -14 },
  { year: "2028", equity: 0, debt: 0, revenue: 58, opex: -18 },
  { year: "2029", equity: 0, debt: 0, revenue: 62, opex: -19 },
  { year: "2030", equity: 0, debt: 0, revenue: 65, opex: -20 },
  { year: "2031", equity: 0, debt: 0, revenue: 68, opex: -21 },
  { year: "2032", equity: 0, debt: 0, revenue: 71, opex: -22 },
  { year: "2033", equity: 0, debt: 0, revenue: 74, opex: -23 },
  { year: "2034", equity: 0, debt: 0, revenue: 78, opex: -24 },
];

const irrSensitivity = [
  { scenario: "Bear", irr: "9.2%", moic: "1.8x", npv: "£12.4m" },
  { scenario: "Base", irr: "14.7%", moic: "2.6x", npv: "£28.1m" },
  { scenario: "Bull", irr: "19.3%", moic: "3.4x", npv: "£44.7m" },
];

const returnMetrics = [
  { label: "Equity IRR (Base)", value: "14.7%", highlight: true },
  { label: "Project IRR", value: "11.2%" },
  { label: "MOIC (Base)", value: "2.6x", highlight: true },
  { label: "Cash Yield (Yr 3+)", value: "7.8% p.a." },
  { label: "Payback Period", value: "6.2 years" },
  { label: "DSCR (Min)", value: "1.35x" },
  { label: "LTV", value: "62%" },
  { label: "Gearing", value: "65% / 35%" },
];

export default function FinancialsTab() {
  const [activeChart, setActiveChart] = useState("cashflow");

  return (
    <div className="space-y-6">
      {/* Return Metrics */}
      <div className="card">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Return Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {returnMetrics?.map((m) => (
            <div key={m?.label} className={`p-3 rounded-md ${m?.highlight ? "bg-primary/8 border border-primary/20" : "bg-muted/50"}`}>
              <p className={`font-data text-xl font-semibold ${m?.highlight ? "text-primary" : "text-foreground"}`}>{m?.value}</p>
              <p className="font-caption text-xs text-muted-foreground mt-1">{m?.label}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Charts */}
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h3 className="font-heading text-lg font-semibold text-foreground">Financial Projections</h3>
          <div className="flex gap-2">
            {["cashflow", "revenue"]?.map((c) => (
              <button
                key={c}
                onClick={() => setActiveChart(c)}
                className={`px-3 py-1.5 rounded text-xs font-caption font-medium transition-all ${activeChart === c ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:text-foreground"}`}
              >
                {c === "cashflow" ? "Cash Flow" : "Revenue"}
              </button>
            ))}
          </div>
        </div>
        <div className="w-full h-64" aria-label="Financial projections chart">
          <ResponsiveContainer width="100%" height="100%">
            {activeChart === "cashflow" ? (
              <BarChart data={cashflowData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fontFamily: "var(--font-data)" }} />
                <YAxis tick={{ fontSize: 11, fontFamily: "var(--font-data)" }} tickFormatter={(v) => `£${v}m`} />
                <Tooltip formatter={(v) => [`£${v}m`]} contentStyle={{ fontFamily: "var(--font-caption)", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12, fontFamily: "var(--font-caption)" }} />
                <Bar dataKey="equity" name="Equity" fill="var(--color-primary)" radius={[2, 2, 0, 0]} />
                <Bar dataKey="revenue" name="Revenue" fill="var(--color-success)" radius={[2, 2, 0, 0]} />
                <Bar dataKey="opex" name="OpEx" fill="var(--color-error)" radius={[2, 2, 0, 0]} />
              </BarChart>
            ) : (
              <AreaChart data={cashflowData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fontFamily: "var(--font-data)" }} />
                <YAxis tick={{ fontSize: 11, fontFamily: "var(--font-data)" }} tickFormatter={(v) => `£${v}m`} />
                <Tooltip formatter={(v) => [`£${v}m`]} contentStyle={{ fontFamily: "var(--font-caption)", fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="var(--color-success)" fill="rgba(5,150,105,0.15)" strokeWidth={2} />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
      {/* Sensitivity Analysis */}
      <div className="card">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Scenario Analysis</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-caption font-semibold text-muted-foreground">Scenario</th>
                <th className="text-right py-2 px-3 font-caption font-semibold text-muted-foreground">Equity IRR</th>
                <th className="text-right py-2 px-3 font-caption font-semibold text-muted-foreground">MOIC</th>
                <th className="text-right py-2 px-3 font-caption font-semibold text-muted-foreground">NPV</th>
              </tr>
            </thead>
            <tbody>
              {irrSensitivity?.map((row) => (
                <tr key={row?.scenario} className={`border-b border-border last:border-0 ${row?.scenario === "Base" ? "bg-primary/5" : ""}`}>
                  <td className="py-3 px-3 font-caption font-medium text-foreground">{row?.scenario}</td>
                  <td className="py-3 px-3 font-data text-right text-foreground">{row?.irr}</td>
                  <td className="py-3 px-3 font-data text-right text-foreground">{row?.moic}</td>
                  <td className="py-3 px-3 font-data text-right text-foreground">{row?.npv}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-caption text-xs text-muted-foreground mt-3 flex items-center gap-1">
          <Icon name="Info" size={12} />
          Sensitivity based on ±15% revenue variance and ±10% capex variance from base case assumptions.
        </p>
      </div>
      {/* Capital Structure */}
      <div className="card">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Capital Structure</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "Senior Debt", amount: "£195m", pct: "65%", color: "bg-[rgba(59,76,76,0.07)]0" },
            { label: "Mezzanine", amount: "£30m", pct: "10%", color: "bg-amber-500" },
            { label: "Equity", amount: "£75m", pct: "25%", color: "bg-emerald-500" },
          ]?.map((item) => (
            <div key={item?.label} className="p-4 rounded-md border border-border">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-3 h-3 rounded-full ${item?.color}`} />
                <span className="font-caption text-sm font-medium text-foreground">{item?.label}</span>
              </div>
              <p className="font-data text-xl font-semibold text-foreground">{item?.amount}</p>
              <p className="font-caption text-xs text-muted-foreground">{item?.pct} of total</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}